<script setup lang="ts">
  import { onBeforeMount, ref } from 'vue';
  import { RouterLink } from 'vue-router';

  import type { Invoice, Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Sheet, SheetContent } from '@ui/components/sheet';

  import { stuut } from '@/api';
  import InvoiceDetailed from '@/components/tasks/InvoiceDetailed.vue';

  interface Props {
    hideCloseBtn?: boolean;
    invoice: Invoice;
    partner?: Partner;
    partnerId?: string;
    partnerName?: string;
  }

  const props = withDefaults(defineProps<Props>(), { hideCloseBtn: true });
  const emits = defineEmits<{
    (e: 'close'): void;
  }>();

  const partner = ref<Partner | undefined>(props.partner);
  const invoice = ref<Invoice>(props.invoice);

  onBeforeMount(async () => {
    if (!props.partner?.id) {
      const pid = props.invoice?.partner?.id || props.invoice?.partner_id;
      if (pid) {
        partner.value = await stuut.partners.retrieve(pid);
      }
    }

    if (!props.invoice?.line_items?.length && props.invoice?.id) {
      invoice.value = await stuut.invoices.retrieve(props.invoice.id);
    }
  });
</script>

<template>
  <Sheet v-bind="$attrs" :key="invoice.id">
    <SheetContent
      class="w-full md:w-150 bg-secondary p-0"
      :hide-close-btn="hideCloseBtn"
      @close="emits('close')"
    >
      <Button
        class="absolute top-0 right-4.5"
        variant="ghost"
        size="icon-sm"
        @click="emits('close')"
      >
        <i class="fas fa-close" />
      </Button>

      <div class="px-6 py-8 h-full overflow-y-auto space-y-4">
        <InvoiceDetailed
          :invoice="invoice"
          :partner="partner"
          :partner-id="partnerId"
          :partner-name="partnerName"
        />

        <div class="flex justify-between px-2">
          <RouterLink
            :to="{
              name: 'invoices.invoice',
              params: { id: props.invoice?.id },
            }"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button class="font-bold" size="inline" variant="link"> View Invoice </Button>
          </RouterLink>

          <RouterLink
            v-if="partner?.id"
            :to="{
              name: 'customers.customer.invoices',
              params: { id: partner?.id },
            }"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button class="font-bold" size="inline" variant="link">
              View {{ partner.name }} invoices
            </Button>
          </RouterLink>
        </div>
      </div>
    </SheetContent>
  </Sheet>
</template>

<style scoped></style>
